/**
 * 用語の読取契約（`listTerms` / `getTermDetail`）— 作り直しの用語 1 型。
 *
 * 用語は**他の要素から指されることが存在理由**である。したがって一覧が出すのは語そのものだけ
 * でなく「どれだけ指されているか」で、詳細が出すのは「誰がどの欄で指しているか」である。
 *
 * **未設定を既定で埋めない。** 区画（`section`）と表現の型（`representation`）は復元が未設定の
 * まま戻す欄で、244 件のうち 79 件が区画未設定である。クラスで埋めると、人が振り分ける対象が
 * 画面から消える。`null` を運び、「未設定」と出すのは画面の仕事にする。
 *
 * ID は number である（作り直しの契約）。案件の指定は業務全体像と同じ形を使い回す
 * （`BusinessMapPjSchema`）— 同じ「番号 ＋ 面」を型だけ変えて 2 つ置くと、どちらが正かを読み手
 * が判断できなくなる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語集と用語詳細（区画・表現・属するクラス・取りうる値・状態遷移・単位・参照元）の入出力契約
 * @graph-connects none
 */
import { z } from 'zod';
import { BusinessMapPjSchema } from './spec-graph-business-map.js';
import { LocaleInputShape, UntranslatedSchema } from './spec-graph-common.js';
/** 用語の読取に載る要素 1 件の番号（用語・クラス・値・単位・動詞・参照元）。 @graph-connects none */
export const TermNodeIdSchema = z.number().int().positive();
/**
 * 用語の読取が指す参照 1 件（属するクラス・取りうる値・単位・状態遷移の両端）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語の読取が返す参照 1 件（id と表示名）
 * @graph-connects none
 */
export const TermRefSchema = z.object({
    id: TermNodeIdSchema,
    label: z.string(),
});
/**
 * `listTerms` の入力。案件と表示言語だけを受ける。
 *
 * 絞り込みを持たないのは、用語の絞り込みが**画面の中の操作**だからである。文字を 1 つ打つ
 * たびにグラフ全体を引き直す形にしない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business `listTerms` の入力（案件・表示言語）
 * @graph-connects none
 */
export const TermListInputSchema = z.object({
    pj: BusinessMapPjSchema.optional(),
    ...LocaleInputShape,
});
/**
 * 案件の中での用語の変わり方 3 値（追加・変更・削除）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語集の行に付く案件の印（追加・変更・削除）の閉集合
 * @graph-connects none
 */
export const TERM_PJ_MARKS = ['created', 'updated', 'deleted'];
/**
 * 用語集の 1 行。
 *
 * `section` / `representation` / `ownerClass` が `null` なのは**未設定**である。表現の型は
 * プロパティの用語だけが持つので、クラスと値の用語では常に `null` になる。
 *
 * `pjMark` は案件を重ねた一覧で、その案件がこの用語を変えたときだけ載る。省けるのは、
 * 案件を重ねない一覧と、印を持たない前の読取の両方で同じ形のまま読めるようにするためである。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語集の 1 行（id・表示名・未翻訳・区画・表現の型・属するクラス・参照される数・案件の印）
 * @graph-connects none
 */
export const TermSummarySchema = z.object({
    termId: TermNodeIdSchema,
    label: z.string(),
    untranslated: UntranslatedSchema,
    section: z.string().nullable(),
    representation: z.string().nullable(),
    ownerClass: TermRefSchema.nullable(),
    referenceCount: z.number().int().nonnegative(),
    pjMark: z.enum(TERM_PJ_MARKS).optional(),
});
/**
 * `listTerms` の出力。
 *
 * ページングしない。244 件は 1 画面に並べて絞り込む規模であり、**絞り込みが画面の中の操作**
 * なので、続きを取りに戻る形にすると押すたびに往復が要る。
 *
 * `removed` は案件を重ねた一覧で、その案件が消した用語（master の姿の行）である。重ねた姿に
 * 居ないので `items` にも `total` にも入れない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business `listTerms` の出力（用語の行と総件数、案件を重ねたなら消した用語）
 * @graph-connects none
 */
export const TermListOutputSchema = z.object({
    items: z.array(TermSummarySchema),
    total: z.number().int().nonnegative(),
    removed: z.array(TermSummarySchema).optional(),
});
/**
 * `getTermDetail` の入力。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business `getTermDetail` の入力（用語の id・案件・表示言語）
 * @graph-connects none
 */
export const TermDetailInputSchema = z.object({
    termId: TermNodeIdSchema,
    pj: BusinessMapPjSchema.optional(),
    ...LocaleInputShape,
});
/**
 * 状態遷移 1 本（今の値・出来事・次の値）。
 *
 * 3 つとも `null` を取る。状態のプロパティは遷移を必須にするが（契約の入力スキーマ）、復元が
 * 戻した用語は遷移の欄ごと未設定で、書かれていても指した先が消えていることがある。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語の状態遷移 1 本（今の値・出来事・次の値）
 * @graph-connects none
 */
export const TermTransitionSchema = z.object({
    from: TermRefSchema.nullable(),
    event: TermRefSchema.nullable(),
    to: TermRefSchema.nullable(),
});
/**
 * 言い換え 1 件。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語の言い換え 1 件（表記と注記）
 * @graph-connects none
 */
export const TermSynonymSchema = z.object({
    text: z.string(),
    note: z.string(),
});
/**
 * この用語を指している要素 1 件。
 *
 * `field` は欄の名前（「判定表のセル」「入力」）である。**どの欄かを落とさない** — 用語は
 * 同じ要素から違う意味で 2 度指されるので（業務の入力にも出力の区分にも同じクラスが載る）、
 * 要素の名前だけでは参照の意味が読めない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語を指している要素 1 件（型・id・表示名・指している欄）
 * @graph-connects none
 */
export const TermReferrerSchema = z.object({
    nodeType: z.string(),
    nodeId: TermNodeIdSchema,
    label: z.string(),
    field: z.string(),
});
/**
 * `getTermDetail` の出力。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business `getTermDetail` の出力（名前・説明・言い換え・備考・区画・表現・属するクラス・取りうる値・状態遷移・単位・参照元）
 * @graph-connects none
 */
export const TermDetailOutputSchema = z.object({
    termId: TermNodeIdSchema,
    label: z.string(),
    untranslated: UntranslatedSchema,
    description: z.string(),
    synonyms: z.array(TermSynonymSchema),
    /** 備考（人が画面から書く補足、1 言語）。無ければ null */
    note: z.string().nullable(),
    section: z.string().nullable(),
    representation: z.string().nullable(),
    ownerClass: TermRefSchema.nullable(),
    values: z.array(TermRefSchema),
    transitions: z.array(TermTransitionSchema),
    unit: TermRefSchema.nullable(),
    referencedBy: z.array(TermReferrerSchema),
});
//# sourceMappingURL=spec-graph-element-terms.js.map